<template>
    <RedesignBaseChart
        ref="baseChartRef"
        :key="showWaitingTimeTable"
        :option="chartOptions"
        :widget="data"
        :classes="chartClasses"
    >
        <template #post>
            <div
                v-if="!!showWaitingTimeTable"
                class="mb-4 -mt-6"
            >
                <redesign-data-table-component
                    :widget-data="data"
                    :columns-config="columnsConfig"
                    :columns="availableColumns"
                    :table-data="tableData"
                    :border="tableBorder"
                    stripe
                    @update-widget="updateWidget"
                    @update-columns-config="updateColumnsConfig"
                    @update-headers-and-columns-config="updateColumnsAndHeadersConfig"
                >
                    <template #cell="{ row, column }">
                        <template v-if="column.prop === 'WaitingTime'">
                            <waiting-time
                                v-if="row.Call"
                                :key="row.Call.ivrid"
                                :call="row.Call"
                                :text-color="'text-white'"
                            />
                        </template>
                        <span
                            v-else
                            v-html="row[column.prop] || '--'"
                        />
                    </template>
                </redesign-data-table-component>
            </div>
        </template>
    </RedesignBaseChart>
</template>

<script setup lang="ts">
import { ColumnConfig, ColumnsObjectConfig, ObjectAnyType, TableHeadersConfig, WidgetExtended } from '@/types/dashboard'
import { hasColumnSpecificAlignment } from '@/helpers/alignmentUtils'
import { computed, defineEmits, toRef } from 'vue'
import { activeGaugeCallColumns } from '@/enum/queueConfigs'
import orderBy from 'lodash/orderBy'
import RedesignBaseChart from '@/components/RedesignComponents/Charts/RedesignBaseChart.vue'
import { QueueGaugeChartColors } from '@/enum/colors'
import WaitingTime from '@/components/Widgets/Data/Queue/WaitingTime.vue'
import useQueues from '@/composables/useQueues'
import useEntityPositiveNegativeFilters from '@/composables/useEntityPositiveNegativeFilters'
import { QUEUE_LIST_PARAMETER_NAME } from '@/enum/widgetTemplateConfigs'
import RedesignDataTableComponent from '@/components/RedesignBaseComponents/RedesignDataTableComponent.vue'
import useTableHeaderTranslations from '@/composables/useTableHeaderTranslations'
import { defaultColumnConfig } from '@/enum/redesignTableConfig'
import { DEFAULT_WARNING_RANGE, DEFAULT_MAX_RANGE } from '@/enum/gradeGauge'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
    data: WidgetExtended,
    editable?: boolean
}>()

const emit = defineEmits<{
    (event: 'on-update', payload: WidgetExtended): void
}>()

const tableBorder = true

const { queues: allQueues, queueWithActiveCalls } = useQueues()
const { makeTableHeaderTranslations, makeTableHeaderTooltipTranslations } = useTableHeaderTranslations()
const widgetData = toRef(props, 'data')

/* Computed */
const maximumRange = computed(() => {
    return props.data?.WidgetLayout?.maximumRange ?? DEFAULT_MAX_RANGE
})

const maxRangeValue = computed(() => {
    if (filteredQueuesCalls.value.length > maximumRange.value) {
        return filteredQueuesCalls.value.length + 2
    }
    return maximumRange.value
})

const warningRange = computed(() => {
    return props.data?.WidgetLayout?.warningRange ?? DEFAULT_WARNING_RANGE
})

const labelFontSize = computed(() => {
    return props.data?.WidgetLayout.labelFontSize ?? 16
})
const showWaitingTimeTable = computed(() => {
    return props.data?.WidgetLayout?.showWaitingTimeTable ?? true
})
const availableColumns = computed(() => {
    return activeGaugeCallColumns.map((column, index) => {
        return {
            ...column,
            label: makeTableHeaderTranslations(column, t(column.label) as string),
            tooltipLabel: makeTableHeaderTooltipTranslations(column, t(column.label) as string),
            width: index === 0 ? '110px' : 'auto'
        }
    })
})

const columnsConfig = computed(() => {
    const savedConfig = props.data.WidgetLayout?.columnsObjectConfig || {}
    const config: Record<string, ColumnConfig> = {}

    availableColumns.value.forEach((column, index) => {
        const defaultConfig: ColumnConfig = {
            ...defaultColumnConfig,
            showFullTextLabel: true,
            width: index === 0 ? 110 : 'auto',
            order: index,
        }

        config[column.prop] = {
            ...defaultConfig,
            ...savedConfig[column.prop],
        }
    })

    return config
})

const { filteredItems: filteredQueuesWithActiveCalls } = useEntityPositiveNegativeFilters(widgetData, QUEUE_LIST_PARAMETER_NAME, queueWithActiveCalls, 'QueueID')

const filteredQueuesCalls = computed(() => {
    return filteredQueuesWithActiveCalls.value.flatMap(queue => queue.Calls)
})
const chartClasses = computed(() => {
    const classes = 'w-full min-h-60'
    const height = !showWaitingTimeTable.value ? 'h-full' : 'h-2/3 max-h-80'
    return `${classes} ${height}`
})

const tableData = computed(() => {
    const data: Array<ObjectAnyType> = []
    let rowsCounter = 0

    filteredQueuesWithActiveCalls.value.forEach((queue) => {
        queue.Calls?.forEach((call) => {
            rowsCounter++
            data.push({
                Id: rowsCounter,
                QueueID: queue.QueueID,
                ///ts error because of the type from sdk (type and property case difference)
                CallerID: call.CallerID,
                Call: call,
                QueueName: queue.QueueName
            })
        })
    })

    return orderBy(data, function (q) {
        return q.Call.JoinTimeStamp_UTC_CLIENT
    }, [ 'asc' ])
})

const chartOptions = computed(() => {
    const minimumRangeColor = props.data?.WidgetLayout?.colors?.minimumRangeColor || QueueGaugeChartColors.minimumRangeColor
    const middleRangeColor = props.data?.WidgetLayout?.colors?.middleRangeColor || QueueGaugeChartColors.middleRangeColor
    const maximumRangeColor = props.data?.WidgetLayout?.colors?.maximumRangeColor || QueueGaugeChartColors.maximumRangeColor
    const warnRange = warningRange.value
    const maxRange = maximumRange.value
    const labelSize = labelFontSize.value
    const axisLabelSize = Math.min(labelSize * 0.75, 24)
    const lineWidth = labelSize < 16 ? 8 : 14
    return {
        grid: {
            left: 'left',
            right: 'right',
            top: 'top',
            bottom: 'bottom',
        },
        series: [
            {
                type: 'gauge',
                min: 0,
                max: maxRangeValue.value,
                splitNumber: maxRangeValue.value,
                itemStyle: {
                    color: function (val: ObjectAnyType) {
                        if (val.value < warnRange) {
                            return minimumRangeColor
                        } else if (val.value >= warnRange && val.value < maxRange)  {
                            return middleRangeColor
                        }
                        return maximumRangeColor
                    },
                    shadowColor: QueueGaugeChartColors.shadowColor,
                    shadowBlur: 10,
                    shadowOffsetX: 2,
                    shadowOffsetY: 2
                },
                progress: {
                    show: true,
                    roundCap: true,
                    width: lineWidth
                },
                pointer: {
                    icon: 'path://M12.8,0.7l12,40.1H0.7L12.8,0.7z',
                    length: '18%',
                    width: 12,
                    offsetCenter: [ 0, '-45%' ],
                },
                axisLine: {
                    roundCap: true,
                    lineStyle: {
                        width: lineWidth
                    },
                },
                axisTick: {
                    splitNumber: 1,
                    lineStyle: {
                        width: 2,
                        color: QueueGaugeChartColors.splitLineColor
                    }
                },
                splitLine: {
                    length: 10,
                    lineStyle: {
                        width: 2,
                        color: QueueGaugeChartColors.splitLineColor
                    }
                },
                axisLabel: {
                    distance: 22,
                    color: QueueGaugeChartColors.splitLineColor,
                    fontSize: axisLabelSize
                },
                title: {
                    offsetCenter: [ 0, '65%' ],
                    fontSize: labelSize,
                    color: QueueGaugeChartColors.splitLineColor
                    // color: 'var(--mainTextColor-color, #263148)'
                },
                detail: {
                    fontSize: labelSize * 1.25,
                    offsetCenter: [ 0, '35%' ],
                    valueAnimation: true,
                    formatter: function (value: number) {
                        return Math.round(value) + ''
                    },
                    color: 'inherit'
                },
                data: [
                    {
                        value: filteredQueuesCalls.value.length,
                        name: 'Calls'
                    }
                ]
            }
        ]
    }
})


/*Methods*/
const updateColumnsConfig = (config: ColumnsObjectConfig) => {
    if (!config || !Object.values(config).length) {
        return
    }
    const hasColumnSpecificAlignmentValue = hasColumnSpecificAlignment(config, props.data.WidgetLayout?.tableColumnsAlignment)
    const updatedWidgetData = {
        ...props.data,
        WidgetLayout: {
            ...(props.data.WidgetLayout || {}),
            hasColumnSpecificAlignment: hasColumnSpecificAlignmentValue,
            columnsObjectConfig: config
        }
    }
    updateWidget(updatedWidgetData)
}
const updateColumnsAndHeadersConfig = (config: ColumnsObjectConfig, tableHeadersConfig: TableHeadersConfig) => {
    const hasColumnSpecificAlignmentValue = hasColumnSpecificAlignment(config, props.data.WidgetLayout?.tableColumnsAlignment)
    const updatedWidgetData = {
        ...props.data,
        WidgetLayout: {
            ...(props.data.WidgetLayout || {}),
            hasColumnSpecificAlignment: hasColumnSpecificAlignmentValue,
            ...(config && Object.values(config) ? { columnsObjectConfig: config } : {}),
            ...(tableHeadersConfig ? { tableHeadersConfig } : {})
        }
    }
    updateWidget(updatedWidgetData)
}

const updateWidget = (widgetData: WidgetExtended) => {
    emit('on-update', widgetData)
}
</script>
